import {
  FETCH_ACTIVE_LANGUAGES_FAIL,
  FETCH_ACTIVE_LANGUAGES_REQUEST,
  FETCH_ACTIVE_LANGUAGES_SUCCESS,
  FETCH_TRANSLATIONS_FAIL,
  FETCH_TRANSLATIONS_REQUEST,
  FETCH_TRANSLATIONS_SUCCESS,
  SET_SELECTED_LANGUAGE,
} from "./type";

const STORAGE_KEY = "selectedLanguageCode";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const saveToStorage = (code) => {
  try {
    if (code) localStorage.setItem(STORAGE_KEY, code);
  } catch {}
};

const readFromStorage = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

// Read URL param — used at boot time only
const getUrlLanguage = () => {
  try {
    return (
      new URLSearchParams(window.location.search)
        .get("language")
        ?.trim()
        ?.toLowerCase() || null
    );
  } catch {
    return null;
  }
};

const initialState = {
  activeLanguages: [],
  selectedLanguageCode: readFromStorage(), // always from localStorage
  selectedLanguage: null,
  translations: {},
  loadingLanguages: false,
  loadingTranslations: false,
  error: null,
  translationCache: {},
};

// ─── Reducer ──────────────────────────────────────────────────────────────────
const languageReducer = (state = initialState, action) => {
  switch (action.type) {
    // ── User manually selected a language from the switcher ───────────────────
    // localStorage is already written by the thunk in action.js,
    // but we write it here too as a safety net.
    case SET_SELECTED_LANGUAGE: {
      const code = action.payload;
      const lang = state.activeLanguages.find(
        (l) => l.languageCode?.toLowerCase() === code?.toLowerCase(),
      );
      saveToStorage(code); // safety net
      return {
        ...state,
        selectedLanguageCode: code,
        selectedLanguage: lang || null,
      };
    }

    // ── Active languages fetching ─────────────────────────────────────────────
    case FETCH_ACTIVE_LANGUAGES_REQUEST:
      return { ...state, loadingLanguages: true, error: null };

    case FETCH_ACTIVE_LANGUAGES_SUCCESS: {
      const activeLanguages = action.payload;

      const localLang = readFromStorage()?.toLowerCase();

      const findLang = (code) =>
        code &&
        activeLanguages.find((l) => l.languageCode?.toLowerCase() === code);

      const localMatch = findLang(localLang);
      const defaultLang =
        activeLanguages.find((l) => l.isDefault) || activeLanguages[0];

      const resolved = localMatch || defaultLang;

      if (resolved) {
        saveToStorage(resolved.languageCode);
        return {
          ...state,
          loadingLanguages: false,
          activeLanguages,
          selectedLanguageCode: resolved.languageCode,
          selectedLanguage: resolved,
        };
      }

      return { ...state, loadingLanguages: false, activeLanguages };
    }

    case FETCH_ACTIVE_LANGUAGES_FAIL:
      return { ...state, loadingLanguages: false, error: action.payload };

    // ── Translations ──────────────────────────────────────────────────────────
    case FETCH_TRANSLATIONS_REQUEST:
      return { ...state, loadingTranslations: true, error: null };

    case FETCH_TRANSLATIONS_SUCCESS:
      return {
        ...state,
        loadingTranslations: false,
        translations: action.payload.translations,
        translationCache: {
          ...state.translationCache,
          [action.payload.languageCode]: action.payload.translations,
        },
      };

    case FETCH_TRANSLATIONS_FAIL:
      return { ...state, loadingTranslations: false, error: action.payload };

    default:
      return state;
  }
};

export default languageReducer;
